import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import { Card, CardContent } from "@nimit9/signet-ui/components/card"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { usePrivacyMask } from "@nimit9/signet-ui/components/privacy-mask"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { type HeatmapData, useHeatmap } from "@/hooks/use-heatmap"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { cn } from "@/lib/cn"
import {
  DAY_LABELS,
  getIntensity,
  getMonthMarkers,
  getMonthWeeks,
  getWeeks,
  MONTH_LABELS,
  WEEKDAY_INITIALS,
} from "./-calendar"
import { buildHeatmapHeadline, dayKey, parseDay } from "./-headline"

export const Route = createFileRoute("/heatmap/")({
  component: HeatmapPage,
})

// Single-hue ramp from the chart tokens, mixed over the surface so it reads
// in both themes: "more spend = stronger colour". Not red — spending isn't an
// error (red is reserved for destructive actions).
const SPEND_CLASSES = [
  "bg-bg-elevated ring-1 ring-inset ring-border-subtle",
  "bg-chart-4/25",
  "bg-chart-4/50",
  "bg-chart-4/75",
  "bg-chart-4",
]
const FUTURE_CLASS = "bg-transparent"

// ─── Pieces ───────────────────────────────────────────────────────────────────

type Days = HeatmapData["days"]

function DayCell({
  dateStr,
  days,
  maxDay,
  today,
  selected,
  onSelect,
  showNumber,
}: {
  dateStr: string
  days: Days
  maxDay: number
  today: string
  selected: boolean
  onSelect: (d: string | null) => void
  showNumber?: boolean
}) {
  const fmt = useCurrencyFormat()
  // Masked: every cell draws at one flat level — shading is the amount.
  const { masked } = usePrivacyMask()
  const amount = days[dateStr]?.total ?? 0
  const intensity = masked ? getIntensity(0, maxDay) : getIntensity(amount, maxDay)
  const isFuture = dateStr > today
  return (
    <button
      type="button"
      data-day={dateStr}
      data-intensity={isFuture ? "future" : intensity}
      aria-pressed={selected}
      aria-label={`${dateStr}: ${amount > 0 ? fmt(amount) : "No spend"}`}
      title={`${dateStr}: ${amount > 0 ? fmt(amount) : "No spend"}`}
      disabled={isFuture}
      onClick={() => onSelect(selected ? null : dateStr)}
      className={cn(
        "flex aspect-square w-full min-w-0 items-center justify-center rounded-[2px] transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isFuture ? FUTURE_CLASS : SPEND_CLASSES[intensity],
        selected && "ring-2 ring-text-primary ring-offset-1 ring-offset-bg-surface",
        showNumber && "rounded-md text-caption tabular-nums",
        showNumber && (intensity >= 3 ? "text-bg-surface" : "text-text-secondary"),
        showNumber && isFuture && "text-text-disabled",
      )}
    >
      {showNumber ? parseDay(dateStr).getDate() : null}
    </button>
  )
}

/** Desktop: the whole year, weeks as columns that share the card width (no fixed px). */
function YearGrid(props: {
  year: number
  days: Days
  maxDay: number
  today: string
  selectedDay: string | null
  onSelect: (d: string | null) => void
}) {
  const weeks = useMemo(() => getWeeks(props.year), [props.year])
  const markers = useMemo(() => getMonthMarkers(weeks), [weeks])
  return (
    <div
      data-testid="heatmap-year-grid"
      className="grid gap-0.5 lg:gap-1"
      style={{ gridTemplateColumns: `auto repeat(${weeks.length}, minmax(0, 1fr))` }}
    >
      <span />
      {weeks.map((_, w) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: week column position is the semantic key
        <span key={w} className="overflow-visible whitespace-nowrap text-label text-text-muted">
          {markers.get(w) ?? ""}
        </span>
      ))}
      {DAY_LABELS.map((label, di) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: seven fixed weekday rows; labels repeat (two "T"s)
        <YearRow key={`${label}-${di}`} label={label} dayIndex={di} weeks={weeks} {...props} />
      ))}
    </div>
  )
}

function YearRow({
  label,
  dayIndex,
  weeks,
  ...cell
}: {
  label: string
  dayIndex: number
  weeks: string[][]
  days: Days
  maxDay: number
  today: string
  selectedDay: string | null
  onSelect: (d: string | null) => void
}) {
  return (
    <>
      <span className="flex items-center pr-1.5 text-label text-text-muted">{label}</span>
      {weeks.map((week, w) => {
        const dateStr = week[dayIndex]
        return dateStr ? (
          <DayCell
            key={dateStr}
            dateStr={dateStr}
            days={cell.days}
            maxDay={cell.maxDay}
            today={cell.today}
            selected={cell.selectedDay === dateStr}
            onSelect={cell.onSelect}
          />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: empty slot position is the semantic key
          <span key={`empty-${w}`} />
        )
      })}
    </>
  )
}

/** Phone: one month at a time, seven columns that fill the width — tappable cells. */
function MonthCalendar({
  year,
  month,
  onMonthChange,
  ...cell
}: {
  year: number
  month: number
  onMonthChange: (m: number) => void
  days: Days
  maxDay: number
  today: string
  selectedDay: string | null
  onSelect: (d: string | null) => void
}) {
  const weeks = useMemo(() => getMonthWeeks(year, month), [year, month])
  const monthName = new Date(year, month, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  })
  return (
    <div data-testid="heatmap-month-calendar" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon-touch"
          className="md:size-8"
          aria-label="Previous month"
          disabled={month === 0}
          onClick={() => onMonthChange(month - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <p className="text-callout font-medium text-text-primary" data-testid="heatmap-month-label">
          {monthName}
        </p>
        <Button
          variant="ghost"
          size="icon-touch"
          className="md:size-8"
          aria-label="Next month"
          disabled={month === 11}
          onClick={() => onMonthChange(month + 1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {WEEKDAY_INITIALS.map((d, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed weekday header
          <span key={i} className="text-center text-label text-text-muted">
            {d}
          </span>
        ))}
        {weeks.flat().map((dateStr, i) =>
          dateStr ? (
            <DayCell
              key={dateStr}
              dateStr={dateStr}
              days={cell.days}
              maxDay={cell.maxDay}
              today={cell.today}
              selected={cell.selectedDay === dateStr}
              onSelect={cell.onSelect}
              showNumber
            />
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: padding slot position is the semantic key
            <span key={`pad-${i}`} />
          ),
        )}
      </div>
    </div>
  )
}

function Legend() {
  return (
    <div className="flex items-center gap-1.5 text-caption text-text-muted" aria-hidden>
      <span>Less</span>
      {SPEND_CLASSES.map((cls) => (
        <span key={cls} className={cn("size-3 rounded-[2px]", cls)} />
      ))}
      <span>More</span>
    </div>
  )
}

function SelectedDay({ day, data }: { day: string; data: Days[string] | undefined }) {
  const label = parseDay(day).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  })
  return (
    <p
      data-testid="heatmap-selected-day"
      className="text-caption text-text-muted"
      aria-live="polite"
    >
      {label} —{" "}
      {data ? (
        <span className="font-medium text-text-primary">
          <Amount value={data.total} /> across {data.count} txn{data.count !== 1 ? "s" : ""}
        </span>
      ) : (
        <span>No spend</span>
      )}
    </p>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function HeatmapPage() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const ownerLabels = useOwnerLabels()
  const now = new Date()
  const currentYear = now.getFullYear()
  const today = dayKey(now)
  const [year, setYear] = useState(currentYear)
  const [owner, setOwner] = useState("all")
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  const [phoneMonth, setPhoneMonth] = useState<number | null>(null)

  const { data, isLoading, isError, error, refetch } = useHeatmap(year, owner)

  const days = data?.days ?? {}
  const stats = data?.stats
  const maxDay = stats?.maxDay || 1

  const headline = data
    ? buildHeatmapHeadline({ year, days: data.days, stats: data.stats, today: now })
    : null

  // Phone calendar opens on the latest month with spend (or this month).
  const spentDays = Object.keys(days)
    .filter((d) => d.startsWith(`${year}-`) && days[d].total > 0)
    .sort()
  const latestMonth =
    spentDays.length > 0
      ? parseDay(spentDays[spentDays.length - 1]).getMonth()
      : year === currentYear
        ? now.getMonth()
        : 11
  const month = phoneMonth ?? latestMonth

  const changeYear = (y: number) => {
    setYear(y)
    setSelectedDay(null)
    setPhoneMonth(null)
  }

  const toolbar = (
    <div
      className="flex flex-wrap items-center justify-between gap-2"
      data-testid="heatmap-toolbar"
    >
      <Select
        value={owner}
        onValueChange={(v) => setOwner(v ?? "all")}
        items={{ all: "Everyone", ...ownerLabels }}
      >
        <SelectTrigger className="min-w-32" aria-label="Owner">
          <SelectValue placeholder="Everyone" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Everyone</SelectItem>
          {Object.entries(ownerLabels).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-touch"
          className="md:size-8"
          aria-label="Previous year"
          onClick={() => changeYear(year - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <span
          className="min-w-12 text-center text-body font-medium text-text-primary"
          data-testid="heatmap-year"
        >
          {year}
        </span>
        <Button
          variant="ghost"
          size="icon-touch"
          className="md:size-8"
          aria-label="Next year"
          disabled={year >= currentYear}
          onClick={() => changeYear(Math.min(year + 1, currentYear))}
        >
          <ChevronRightIcon />
        </Button>
      </div>
    </div>
  )

  const monthTotals = MONTH_LABELS.map((label, i) => {
    const prefix = `${year}-${String(i + 1).padStart(2, "0")}`
    const entries = Object.entries(days).filter(([d]) => d.startsWith(prefix))
    const total = entries.reduce((s, [, v]) => s + v.total, 0)
    return { label, index: i, total, activeDays: entries.length }
  })

  return (
    <>
      <Header title="Spending heatmap" showPeriod={false} />
      <PageWrapper>
        <div
          className="flex min-w-0 flex-col gap-4 md:gap-5"
          data-testid="heatmap-page"
          data-layout={isDesktop ? "desktop" : "phone"}
        >
          {toolbar}

          {isLoading ? (
            <Skeleton className="h-64 animate-pulse ring-1 ring-border-subtle" />
          ) : isError ? (
            <ErrorState
              message={error instanceof Error ? error.message : undefined}
              onRetry={refetch}
            />
          ) : (
            <>
              {headline && (
                <AnswerHeadline
                  headline={toSignetHeadline(headline)}
                  renderValue={renderAnswerAmount}
                />
              )}

              {stats && (
                <StatStrip
                  isDesktop={isDesktop}
                  items={[
                    { label: "Total spent", value: <Amount value={stats.totalSpent} /> },
                    { label: "Days with spend", value: String(stats.daysWithSpend) },
                    { label: "Avg daily", value: <Amount value={stats.avgDaily} /> },
                    { label: "Highest day", value: <Amount value={stats.maxDay} /> },
                  ]}
                />
              )}

              <Card className="min-w-0">
                <CardContent className="flex min-w-0 flex-col gap-4">
                  {isDesktop ? (
                    <YearGrid
                      year={year}
                      days={days}
                      maxDay={maxDay}
                      today={today}
                      selectedDay={selectedDay}
                      onSelect={setSelectedDay}
                    />
                  ) : (
                    <MonthCalendar
                      year={year}
                      month={month}
                      onMonthChange={setPhoneMonth}
                      days={days}
                      maxDay={maxDay}
                      today={today}
                      selectedDay={selectedDay}
                      onSelect={setSelectedDay}
                    />
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Legend />
                    {selectedDay && <SelectedDay day={selectedDay} data={days[selectedDay]} />}
                  </div>
                </CardContent>
              </Card>

              {stats && stats.daysWithSpend > 0 && (
                <Card className="min-w-0 gap-0 py-0" data-testid="heatmap-monthly-totals">
                  <h2 className="border-b border-border-subtle px-4 py-3 text-callout font-semibold text-text-primary md:px-5">
                    Monthly totals
                  </h2>
                  <ul className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 md:p-4">
                    {monthTotals.map((m) => {
                      const body = (
                        <>
                          <span className="text-caption font-normal text-text-muted">
                            {m.label}
                          </span>
                          {m.total > 0 ? (
                            <Amount value={m.total} className="text-body font-medium" />
                          ) : (
                            <span className="text-body text-text-muted">—</span>
                          )}
                          {m.activeDays > 0 && (
                            <span className="text-label font-normal text-text-muted">
                              {m.activeDays}d · avg{" "}
                              <Amount value={Math.round(m.total / m.activeDays)} />
                              /d
                            </span>
                          )}
                        </>
                      )
                      return (
                        <li key={m.label}>
                          {isDesktop ? (
                            <div className="flex flex-col gap-0.5 rounded-lg px-3 py-2 ring-1 ring-border-subtle">
                              {body}
                            </div>
                          ) : (
                            // Phone: a month total jumps the calendar above to that month.
                            <Button
                              variant="outline"
                              aria-label={`Show ${m.label} ${year}`}
                              aria-pressed={m.index === month}
                              onClick={() => setPhoneMonth(m.index)}
                              className={cn(
                                "h-auto w-full flex-col items-start gap-0.5 px-3 py-2 text-left",
                                m.index === month && "ring-1 ring-border-strong",
                              )}
                            >
                              {body}
                            </Button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </Card>
              )}
            </>
          )}
        </div>
      </PageWrapper>
    </>
  )
}
